<script setup lang="ts">
/**
 * BookingsCTA Component
 * 
 * Reusable call-to-action component for booking consultations.
 * Integrates with Nuxt UI UButton and supports analytics tracking.
 * 
 * @component
 * @example
 * ```vue
 * <BookingsCTA
 *   buttonText="Book Consultation"
 *   bookingsUrl="https://calendly.com/qiksolve"
 *   variant="primary"
 *   location="hero"
 *   product="review"
 * />
 * ```
 */

import { isValidCtaUrl } from '../../lib/validation/ctaValidation'
import { trackCtaClick } from '../../lib/tracking/ctaTracking'

/**
 * Props interface for BookingsCTA component
 */
export interface BookingsCTAProps {
  /**
   * Text displayed on the button
   */
  buttonText: string
  
  /**
   * URL to navigate to when button is clicked
   * Must be a valid http/https URL (javascript: and data: URIs blocked for security)
   */
  bookingsUrl: string
  
  /**
   * Visual variant of the button (Nuxt UI UButton variant)
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary'
  
  /**
   * Location identifier for analytics tracking
   * Used to track which page section the CTA was clicked from
   * @default 'mid'
   */
  location?: 'hero' | 'mid' | 'footer'
  
  /**
   * Product context for analytics tracking
   * Identifies which product page the CTA is on
   */
  product: string
  
  /**
   * Whether to open the link in a new tab
   * @default false
   */
  target?: '_blank' | '_self'
}

const props = withDefaults(defineProps<BookingsCTAProps>(), {
  variant: 'primary',
  location: 'mid',
  target: '_self',
})

/**
 * Custom event emitted when CTA button is clicked
 * 
 * @event bookings-cta-clicked
 * @param {Object} payload - Event payload
 * @param {string} payload.location - Location identifier (hero/mid/footer)
 * @param {string} payload.url - Bookings URL clicked
 * @param {string} payload.buttonText - Button text clicked
 */
const emit = defineEmits<{
  'bookings-cta-clicked': [payload: {
    location: string
    url: string
    buttonText: string
  }]
}>()

/**
 * Validate URL and emit click event with tracking metadata
 * 
 * Logs and aborts when bookingsUrl is invalid (blocks javascript:, data: URIs)
 */
const handleBookingClick = () => {
  // Validate URL to prevent XSS attacks
  if (!isValidCtaUrl(props.bookingsUrl)) {
    console.error('[BookingsCTA] Invalid bookings URL:', props.bookingsUrl)
    return
  }
  
  // Track CTA click with analytics (T003)
  trackCtaClick(props.location, props.product, props.buttonText)
  
  // Emit custom event for parent component (if needed)
  emit('bookings-cta-clicked', {
    location: props.location,
    url: props.bookingsUrl,
    buttonText: props.buttonText,
  })
}

/**
 * Compute rel attribute for external links opened in new tab
 * Prevents security vulnerabilities (tabnabbing)
 */
const relAttribute = computed(() => {
  return props.target === '_blank' ? 'noopener noreferrer' : undefined
})
</script>

<template>
  <UButton
    :to="bookingsUrl"
    :target="target"
    :rel="relAttribute"
    :variant="variant"
    :data-location="location"
    :aria-label="`${buttonText} - Opens ${target === '_blank' ? 'in new tab' : 'in same tab'}`"
    @click="handleBookingClick"
  >
    {{ buttonText }}
  </UButton>
</template>

<style scoped>
/* No custom styles needed - Nuxt UI UButton provides complete styling */
</style>
